import React, { useEffect, lazy, Suspense, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Route } from 'react-router-dom';
import { CanI } from '@redux-action/permissions/permissions';
import { v4 as uuid } from 'uuid';

import Spinner from '../comps/Spinner';

import FlashBox from '../comps/FlashBox';
const Loadable = Component => props => (
    <Suspense fallback={<Spinner />}>
        <Component {...props} />
    </Suspense>
);

const OrderRoutes = Loadable(lazy(() => import('./OrderRoutes')));
const NotFound = Loadable(lazy(() => import('../views/404')));
const VendorBillRoutes = Loadable(lazy(() => import('./VendorBillRoutes')));
const PropertyRoutes = Loadable(lazy(() => import('./PropertyRoutes')));
const AutomobilesRoutes = Loadable(lazy(() => import('./AutomobilesRoutes')));
const ContactRoutes = Loadable(lazy(() => import('./ContactRoutes')));
const ContactTypeRoutes = Loadable(lazy(() => import('./ContactTypeRoutes')));
const AmenityRoutes = Loadable(lazy(() => import('./AmenityRoutes')));
const ReportRoutes = Loadable(lazy(() => import('./ReportRoutes')));
const GroupRoutes = Loadable(lazy(() => import('./GroupRoutes')));
const RoleRoutes = Loadable(lazy(() => import('./RoleRoutes')));
const OrderLineItemRoutes = Loadable(
    lazy(() => import('./OrderLineItemRoutes'))
);
const PreseizureRoutes = Loadable(
    lazy(() => import('./PreseizureRoutes'))
);
const TitleRoutes = Loadable(
    lazy(() => import('./TitleRoutes'))
);
const USMSDashboardRoutes = Loadable(
    lazy(() => import('./USMSDashboardRoutes'))
);
const CustomerServiceRoutes = Loadable(
    lazy(() => import('./CustomerServiceRoutes'))
);

const VendorRoutes = Loadable(lazy(() => import("./VendorRoutes")));
const LogRoutes = Loadable(lazy(() => import("./LogRoutes")));
const TangibleRoutes = Loadable(lazy(() => import("./TangibleRoutes")));
const IntangibleRoutes = Loadable(lazy(() => import("./IntangibleRoutes")));
const LeaseRoutes = Loadable(lazy(() => import("./LeaseRoutes")));

// const OrderRoutes = lazy(() => import("./OrderRoutes"));
const VideoRoutes = Loadable(lazy(() => import('./VideoRoutes')));
const AccountCodeRoutes = Loadable(lazy(() => import('./AccountCodeRoutes')));
const Account = Loadable(lazy(() => import('../views/Account')));
const TrustCenter = Loadable(lazy(() => import('../views/TrustCenter/TrustCenter')));
const Invoices = Loadable(lazy(() => import('../views/Invoices')));
const Contracts = Loadable(lazy(() => import('../views/Contracts')));

const Tasks = Loadable(lazy(() => import('../views/Tasks')));

const Bugs = Loadable(lazy(() => import('../views/Bugs')));
const UserReports = Loadable(lazy(() => import('../views/UserReports')));

import ConfirmModal from '../comps/Datatable/Modals/ConfirmModal';
import { setRoute } from '@redux-action/misc/quickAction';
import AdminRoutes from './AdminRoutes';
const SystemInformationState = Loadable(
    lazy(() =>
        import(
            '../views/Admin/components/SystemInformation/context/SystemInformationState'
        )
    )
);

/**
 *
 * @param CanI
 * @param setRoute
 * @param loadingComponentPermission
 * @param rest
 * @returns {*}
 * @constructor
 */
const PrivateRoute = ({
    CanI,
    setRoute,
    loadingComponentPermission,
    ...rest
}) => {
    //  ...rest is a spread method use to pull everything else from props
    const { computedMatch: { params: { component, verb, id, secondid } } } = rest;

    useEffect(() => {
        let newComponent = component;
        let newVerb = verb;
        let newId = id;

        if (component === "orders" && verb === "line_items") {
            newComponent = component + "/" + verb;
            newVerb = id;
            newId = secondid;
        }

        setRoute({
            component: newComponent,
            verb: newVerb,
            id: newId
        });
    }, [
        component,
        verb,
        id
    ]);

    if (loadingComponentPermission) {
        return <Spinner />;
    }
    return (
        <Fragment>
            <FlashBox />
            <ConfirmModal component={component} />
            <Route
                {...rest}
                render={props => {
                    //  wait for InitPermission to finish loading before move to the next step
                    switch (component) {
                        case '404':
                            return <NotFound {...props} />;
                        case 'account':
                            return (
                                <Account {...props} CanI={CanI} key={uuid()} />
                            );
                        case 'trust_center':
                            return (
                                <TrustCenter {...props} CanI={CanI} key={uuid()} />
                            )
                        case 'properties':
                            return <PropertyRoutes {...props} key={uuid()} />;
                        case 'automobiles':
                            return (
                                <AutomobilesRoutes {...props} key={uuid()} />
                            );
                        case 'leases':
                            return <LeaseRoutes {...props} key={uuid()} />;
                        case 'tangibles':
                            return <TangibleRoutes {...props} key={uuid()} />;
                        case "intangibles":
                            return <IntangibleRoutes {...props} key={uuid()} />;
                        case "vendor_bills":
                            return <VendorBillRoutes {...props} key={uuid()} />;
                        case "contacts": {
                            if (verb !== "types") {
                                return (
                                    <ContactRoutes {...props} key={uuid()} />
                                );
                            }
                            return (
                                <ContactTypeRoutes
                                    {...props}
                                    key={uuid()}
                                />
                            );
                        }
                        case 'vendors':
                            return (
                                <VendorRoutes
                                    {...props}
                                    CanI={CanI}
                                    key={uuid()}
                                />
                            );
                        case 'invoices':
                            return (
                                <Invoices {...props} CanI={CanI} key={uuid()} />
                            );
                        case "orders":
                            if (verb !== "line_items") {
                                return (
                                    <OrderRoutes
                                        {...props}
                                        CanI={CanI}
                                        key={uuid()}
                                    />
                                );
                            }
                            return (
                                <OrderLineItemRoutes
                                    {...props}
                                    key={uuid()}
                                />
                            );
                        case "preseizure":
                            return <PreseizureRoutes {...props} key={uuid()} />;
                        case "title":
                            return <TitleRoutes {...props} key={uuid()} />;
                        case "customer-service":
                            return <CustomerServiceRoutes {...props} key={uuid()} />;
                        case "usms-dashboard":
                            return <USMSDashboardRoutes {...props} key={uuid()} />;
                        case "groups":
                            return <GroupRoutes {...props} key={uuid()} />;
                        case 'contracts':
                            return (
                                <Contracts
                                    {...props}
                                    CanI={CanI}
                                    key={uuid()}
                                />
                            );
                        case 'tasks':
                            return (
                                <Tasks {...props} CanI={CanI} key={uuid()} />
                            );
                        case 'logs':
                            return (
                                <LogRoutes
                                    {...props}
                                    CanI={CanI}
                                    key={uuid()}
                                />
                            );
                        case 'bugs':
                            return <Bugs {...props} CanI={CanI} key={uuid()} />;
                        case 'user_reports':
                            return <UserReports {...props} key={uuid()} />;
                        case 'reports':
                            return <ReportRoutes {...props} key={uuid()} />;
                        case 'roles':
                            return <RoleRoutes {...props} key={uuid()} />;
                        case 'amenities':
                            return <AmenityRoutes {...props} key={uuid()} />;
                        case 'account_codes':
                            return <AccountCodeRoutes {...props} />;
                        case 'videos':
                            return <VideoRoutes {...props} key={uuid()} />;
                        case 'admin':
                            return (
                                <SystemInformationState>
                                    <AdminRoutes
                                        CanI={CanI}
                                        {...props}
                                        key={uuid()}
                                    />
                                </SystemInformationState>
                            );

                        default:
                            return <Spinner />;
                    }
                }}
            />
        </Fragment>
    );
};

PrivateRoute.propTypes = {
    //  Function - checking user permission
    CanI: PropTypes.func.isRequired,
    //  Function - setting current route component - verb - id
    setRoute: PropTypes.func.isRequired
};

const mapStateToProps = state => ({ loadingComponentPermission: state.permissions.loadingComponentPermission });
export default connect(mapStateToProps, {
    CanI,
    setRoute
})(PrivateRoute);
